import React, { useEffect, useRef, useState } from 'react';

import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import { useIdSearchableFilterOptions } from '../../../common/entityId.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useFindProducts } from '../Product.hooks';
import { Product } from '../Product.interface';

export interface ProductAutocompleteProps {
  productId?: number;
  className?: string;
  disabled?: boolean;
  onChange?: (product?: Product) => void;
}

const ProductAutocomplete: React.FC<ProductAutocompleteProps> = ({
  productId,
  className,
  disabled,
  onChange
}) => {
  const mountRef = useRef(true);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<Product | null>(null);
  const { productList, isLoading, findProducts } = useFindProducts();
  const filterOptions = useIdSearchableFilterOptions<Product>(
    WorkflowEntity.Product,
    'name'
  );
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const params: ApiParams = { limit: 100 };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    findProducts(params);
  }, [findProducts, debouncedSearch]);

  useEffect(() => {
    if (mountRef.current && productId && productList?.data) {
      mountRef.current = false;
      setSelected(productList.data.find(p => p.id === productId) || null);
    }
  }, [productId, productList?.data]);

  return (
    <Autocomplete
      fullWidth
      className={className}
      disabled={disabled}
      loading={isLoading}
      getOptionLabel={product => product.name}
      getOptionSelected={(product, value) => product.id === value?.id}
      options={productList?.data || []}
      value={selected}
      filterOptions={filterOptions}
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_, value) => {
        setSelected(value);
        onChange?.(value || undefined);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          label='Product'
          variant='outlined'
          autoComplete='new-password'
          InputProps={{
            ...params.InputProps,
            autoComplete: 'new-password' // disable browser autcomplete
          }}
        />
      )}
    />
  );
};

export default ProductAutocomplete;
